import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from "@mui/material";
import CardActions from "@mui/material/CardActions";
import settings from "app/settings";
import I18n from "i18n-js";
import QueryString from "qs";
import { useEffect, useState } from "react";
import {
  Autocomplete,
  Field,
  FieldsContainer,
  globalMessageHandler,
  TextField,
} from "../../../common";
import ColorPicker from "../../../components/ColorPicker/ColorPicker";

export interface WaiverWidgetProps {
  waiverId: string;
}

export default function WaiverWidget({ waiverId }: WaiverWidgetProps) {
  const [buttonText, setButtonText] = useState<string>(
    I18n.t("Sharing.tools.waiverWidget.defaultButtonText")
  );
  const [code, setCode] = useState<string>("");
  const [fontSize, setFontSize] = useState<number>(15);
  const [fontFamily, setFontFamily] = useState<string>("Arial");
  const [textColor, setTextColor] = useState<string>("#FFFFFF");
  const [alignment, setAlignment] = useState<string>("right");
  const [backgroundColor, setBackgroundColor] = useState<string>("#FF9800");
  const fontSizes = [
    { label: I18n.t("Sharing.tools.waiverWidget.fontSize.large"), value: 20 },
    { label: I18n.t("Sharing.tools.waiverWidget.fontSize.medium"), value: 15 },
    { label: I18n.t("Sharing.tools.waiverWidget.fontSize.small"), value: 10 },
  ];
  const fontFamilies = [
    {
      label: I18n.t("Sharing.tools.waiverWidget.fontFamily.arial"),
      value: "Arial",
    },
    {
      label: I18n.t("Sharing.tools.waiverWidget.fontFamily.monospace"),
      value: "monospace",
    },
  ];
  const alignments = [
    {
      label: I18n.t("Sharing.tools.waiverWidget.alignment.right"),
      value: "right",
    },
    {
      label: I18n.t("Sharing.tools.waiverWidget.alignment.left"),
      value: "left",
    },
    {
      label: I18n.t("Sharing.tools.waiverWidget.alignment.center"),
      value: "center",
    },
  ];

  const query = QueryString.stringify({
    buttonText,
    fontFamily,
    fontSize,
    alignment,
    backgroundColor,
    textColor,
    waiverId,
  });
  const baseWidgetScriptSrc = `${settings.apiHost}/waiver-widget/js`;
  const widgetScriptSrc = `${baseWidgetScriptSrc}?${query}`;
  const baseWidgetStyleSrc = `${settings.apiHost}/waiver-widget/css`;

  function removeWidget() {
    // Remove old scripts
    const scripts = document.querySelectorAll("script[src]");
    scripts.forEach(element => {
      const script = element as HTMLScriptElement;
      try {
        const url = new URL(script.src);
        if (url.origin + url.pathname === baseWidgetScriptSrc) {
          script.parentNode?.removeChild(script);
          //console.log(`Removed script: ${script.src}`);
        }
      } catch (e) {}
    });

    // Remove old css
    const links = document.querySelectorAll("link[href]");
    links.forEach(element => {
      const link = element as HTMLLinkElement;
      try {
        const url = new URL(link.href);
        //console.log(url.origin + url.pathname);
        //console.log(baseWidgetStyleSrc);
        if (url.origin + url.pathname === baseWidgetStyleSrc) {
          link.parentNode?.removeChild(link);
          //console.log(`Removed link: ${link.href}`);
        }
      } catch (e) {}
    });

    // Remove widget containers
    const containers = document.getElementsByClassName("signwaivers-container");
    if (containers.length > 0) {
      Array.from(containers).forEach(container => container.remove());
    }
  }

  function reloadWidget() {
    removeWidget();

    // Create new one with the updated src
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = widgetScriptSrc;

    document.body.appendChild(script);
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setCode(
        `<script src="${settings.apiHost}/waiver-widget/js?${query}" type="text/javascript"></script>`
          // Remove formatting so it will appear not as code format
          .replace(/\n/g, " ")
          .replace(/\s\s+/g, " ")
          .trim()
      );

      reloadWidget();
    }, 200);

    return () => {
      clearTimeout(handler);
      removeWidget();
    };
  }, [buttonText, fontSize, fontFamily, alignment, textColor, backgroundColor]);

  return (
    <Card>
      <CardHeader title={I18n.t("Sharing.tools.waiverWidget.title")} />
      <CardContent>
        <Grid container>
          <Grid
            item
            md={6}
            sm={12}
            sx={{ paddingRight: { md: "5px", sm: "0px" } }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "10px" }}>
              {I18n.t("Sharing.tools.waiverWidget.customizeWaiverWidget")}
            </Typography>
            <FieldsContainer>
              <Field fullWidth>
                <TextField
                  label={I18n.t(
                    "Sharing.tools.waiverWidget.attributes.buttonText"
                  )}
                  value={buttonText}
                  useDelayedKeystrokes={false}
                  onChange={(evt: any) => setButtonText(evt.target.value)}
                />
              </Field>
              <Field fullWidth>
                <Autocomplete
                  label={I18n.t(
                    "Sharing.tools.waiverWidget.attributes.fontSize"
                  )}
                  value={fontSize}
                  options={fontSizes}
                  onChange={(evt: any) => setFontSize(evt.target.value)}
                />
              </Field>
              <Field fullWidth>
                <Autocomplete
                  label={I18n.t(
                    "Sharing.tools.waiverWidget.attributes.alignment"
                  )}
                  value={alignment}
                  options={alignments}
                  onChange={(evt: any) => setAlignment(evt.target.value)}
                />
              </Field>
              <Field fullWidth>
                <Autocomplete
                  label={I18n.t(
                    "Sharing.tools.waiverWidget.attributes.fontFamily"
                  )}
                  value={fontFamily}
                  options={fontFamilies}
                  onChange={(evt: any) => setFontFamily(evt.target.value)}
                />
              </Field>
              <Field
                half
                sx={{ flex: 1, alignItems: "center", justifyItems: "center" }}
              >
                <ColorPicker
                  label={I18n.t(
                    "Sharing.tools.waiverWidget.attributes.backgroundColor"
                  )}
                  value={backgroundColor}
                  setValue={setBackgroundColor}
                />
              </Field>
              <Field
                half
                sx={{ flex: 1, alignItems: "center", justifyItems: "center" }}
              >
                <ColorPicker
                  label={I18n.t(
                    "Sharing.tools.waiverWidget.attributes.textColor"
                  )}
                  value={textColor}
                  setValue={setTextColor}
                />
              </Field>
            </FieldsContainer>
          </Grid>
          <Grid
            item
            md={6}
            sm={12}
            sx={{ paddingLeft: { md: "5px", sm: "0px" } }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "10px" }}>
              {I18n.t("Sharing.tools.waiverWidget.preview")}
            </Typography>
            <Typography variant="subtitle2" sx={{ marginBottom: "10px" }}>
              {I18n.t("Sharing.tools.waiverWidget.previewDescription")}
            </Typography>
            <FieldsContainer>
              <Field fullWidth>
                <TextField
                  label={I18n.t("Sharing.tools.waiverWidget.attributes.code")}
                  value={code}
                  fullWidth
                  multiline
                  editable={false}
                />
              </Field>
            </FieldsContainer>
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Button
          startIcon={<ContentCopyIcon />}
          onClick={() => {
            navigator.clipboard.writeText(code);
            globalMessageHandler.success(I18n.t("App.codeCopied"));
          }}
        >
          {I18n.t("Sharing.tools.waiverWidget.copyButton")}
        </Button>
      </CardActions>
    </Card>
  );
}
